import * as React from 'react'

import { insertImage, insertMediaEmbed } from '@platejs/media'
import { RichTextField } from '@buffer-mono/popcorn/rich-text'

import {
  Dialog,
  Button,
  Input,
  Label,
  Flex,
  Form,
  toast,
} from '@buffer-mono/popcorn'
import {
  FileUploadConfig,
  UploadSource,
  UploadType,
  useUploader,
} from '@buffer-mono/uploader'

import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'

import {
  createChangelogEditor,
  emptyChangelogValue,
  isAllowedMediaUrl,
  serializeChangelogToHtml,
} from './createChangelogEditor'
import type { ChangelogEditor, ChangelogValue } from './createChangelogEditor'
import { EditorToolbar } from './EditorToolbar'

import styles from './ChangelogContentEditor.module.css'

import type { JSX } from 'react'

export interface ChangelogContent {
  html: string
  slateValue: ChangelogValue
}

interface ChangelogContentEditorProps {
  initialValue?: ChangelogValue
  onChange?: (data: ChangelogContent) => void
  placeholder?: string
}

const imageRestrictions = {
  maxNumberOfFiles: 1,
  allowedFileTypes: ['.jpg', '.jpeg', '.png', '.webp', '.gif'],
  uploadConfig: FileUploadConfig.IMAGE,
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

function parseVideoUrl(url: string): string | null {
  if (!isAllowedMediaUrl(url)) return null

  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/,
  )
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`
  }

  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/)
  if (loomMatch) {
    return `https://www.loom.com/embed/${loomMatch[1]}`
  }

  if (url.includes('/embed/')) {
    return url
  }

  return null
}

function HiddenImageInput({
  fileInputRef,
}: {
  fileInputRef: React.RefObject<HTMLInputElement>
}): JSX.Element {
  return (
    <input
      type="file"
      ref={fileInputRef}
      className={styles.hiddenInput}
      accept="image/jpeg,image/png,image/webp,image/gif"
      aria-label="Upload image"
    />
  )
}

function EmbedUrlDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (url: string) => void
}): JSX.Element {
  const [urlInput, setUrlInput] = React.useState('')

  const handleSubmit = (): void => {
    if (urlInput.trim()) {
      onSubmit(urlInput.trim())
    }
    onOpenChange(false)
    setUrlInput('')
  }

  const handleOpenChange = (isOpen: boolean): void => {
    onOpenChange(isOpen)
    if (!isOpen) {
      setUrlInput('')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size="small">
        <Dialog.Header>
          <Dialog.Title>Insert Video</Dialog.Title>
          <Dialog.Description>
            Paste a YouTube or Loom URL to embed a video.
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Body>
          <Flex direction="column" gap="2xs">
            <Label htmlFor="embed-url">Video URL</Label>
            <Input
              id="embed-url"
              className={styles.fullWidthInput}
              placeholder="https://www.youtube.com/watch?v=..."
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit()
                }
              }}
            />
          </Flex>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Close>
            <Button variant="tertiary">Cancel</Button>
          </Dialog.Close>
          <Button variant="primary" onClick={handleSubmit}>
            Insert
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

/**
 * Uploads an image File to S3 and inserts the resulting URL into the editor.
 * Every image-insert surface (toolbar, paste, drop) routes through this so the
 * editor never holds a data: URL, which would otherwise inflate the serialized
 * `content` past the API gateway's 100 KB JSON body limit.
 */
function useChangelogImageUpload(editor: ChangelogEditor): {
  uploadAndInsert: (file: File) => Promise<void>
} {
  const accountId = useAccountId()
  const organizationId = useOrganizationId()

  const uploader = useUploader({
    id: UploadType.postAsset,
    userId: accountId ?? '',
    organizationId: organizationId ?? '',
    fileRestrictions: imageRestrictions,
  })

  const uploadAndInsert = React.useCallback(
    async (file: File): Promise<void> => {
      if (!file.type.startsWith('image/')) return

      if (file.size > MAX_FILE_SIZE) {
        toast.error('Image must be under 10 MB.')
        return
      }

      try {
        const result = await uploader.upload([file], {
          source: UploadSource.filePicker(),
        })

        if (result.successful.length) {
          const imageUrl = result.successful[0].uploadURL
          insertImage(editor, imageUrl)
          toast.success('Image inserted!')
        } else {
          toast.error('Image upload failed. Please try again.')
        }
      } catch {
        toast.error('Image upload failed. Please try again.')
      }

      uploader.uppyInstance.cancelAll()
    },
    [editor, uploader],
  )

  return { uploadAndInsert }
}

/**
 * Rich text editor for changelog content.
 *
 * Features:
 * - Bold, italic, underline formatting
 * - Headings (H2 only)
 * - Bullet and numbered lists
 * - Image upload
 * - Video embeds (YouTube, Loom)
 * - Notion-style shortcuts
 *
 * @example
 * ```tsx
 * <ChangelogContentEditor
 *   initialValue={existingContent}
 *   onChange={(data) => console.log(data.html, data.slateValue)}
 *   placeholder="Write your changelog..."
 * />
 * ```
 */
export function ChangelogContentEditor({
  initialValue,
  onChange,
  placeholder = 'Write your changelog entry...',
}: ChangelogContentEditorProps): JSX.Element {
  const [embedDialogOpen, setEmbedDialogOpen] = React.useState(false)

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Create editor once. `useRef(createChangelogEditor())` would invoke the
  // factory on every render and discard the result; initialize lazily instead.
  const editorRef = React.useRef<ReturnType<
    typeof createChangelogEditor
  > | null>(null)
  if (editorRef.current === null) {
    editorRef.current = createChangelogEditor(
      initialValue ?? emptyChangelogValue,
    )
  }
  const editor = editorRef.current

  const { uploadAndInsert } = useChangelogImageUpload(editor)

  const handleInsertImage = React.useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleInsertEmbed = React.useCallback(() => {
    setEmbedDialogOpen(true)
  }, [])

  const handleEmbedSubmit = (url: string): void => {
    const parsedUrl = parseVideoUrl(url)
    if (!parsedUrl) {
      toast.error('Could not parse video URL. Try YouTube or Loom links.')
      return
    }

    if (!editor.selection) editor.tf.select(editor.api.end([]))
    insertMediaEmbed(editor, { url: parsedUrl })
    toast.success('Video embedded!')
  }

  React.useEffect(() => {
    const input = fileInputRef.current
    if (!input) return

    const handleChange = (): void => {
      if (!input.files?.length) return
      void uploadAndInsert(input.files[0])
      input.value = ''
    }

    input.addEventListener('change', handleChange)
    return (): void => {
      input.removeEventListener('change', handleChange)
    }
  }, [uploadAndInsert])

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>): void => {
      const file = Array.from(event.clipboardData?.files ?? []).find((f) =>
        f.type.startsWith('image/'),
      )
      if (!file) return
      event.preventDefault()
      event.stopPropagation()
      void uploadAndInsert(file)
    },
    [uploadAndInsert],
  )

  const handleDrop = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      const file = Array.from(event.dataTransfer?.files ?? []).find((f) =>
        f.type.startsWith('image/'),
      )
      if (!file) return
      event.preventDefault()
      event.stopPropagation()
      void uploadAndInsert(file)
    },
    [uploadAndInsert],
  )

  return (
    <>
      <HiddenImageInput fileInputRef={fileInputRef} />

      <EmbedUrlDialog
        open={embedDialogOpen}
        onOpenChange={setEmbedDialogOpen}
        onSubmit={handleEmbedSubmit}
      />

      <RichTextField.Root
        editor={editor}
        onValueChange={({ value: newValue }) => {
          if (onChange) {
            const html = serializeChangelogToHtml(newValue)
            onChange({ html, slateValue: newValue })
          }
        }}
        // See the matching comment on Composer's EditorSurface: every mounted
        // Plate instance shares one global instance counter, so having both
        // this and Composer mounted at once is a false positive on both sides.
        suppressInstanceWarning
      >
        <EditorToolbar
          onInsertImage={handleInsertImage}
          onInsertEmbed={handleInsertEmbed}
        />
        <Form.Control>
          <RichTextField.Editable
            placeholder={placeholder}
            spellCheck
            className={styles.editable}
            onPasteCapture={handlePaste}
            onDropCapture={handleDrop}
          />
        </Form.Control>
      </RichTextField.Root>
    </>
  )
}
